import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { isPermission, type Permission } from '@/lib/permissions';
import { useTenant } from '@/hooks/useTenant';

export interface UserWithRole {
  id: string;
  email: string;
  full_name: string;
  phone: string | null;
  created_at: string;
  last_login_at: string | null;
  role: string;
  /** Explicit grants (Settings → Users → Permissions). Admins hold everything regardless. */
  permissions: Permission[];
  deactivated_at: string | null;
}

async function fetchUsers(includeDeactivated: boolean): Promise<UserWithRole[]> {
  const { data: profiles, error: profilesError } = await supabase
    .from('profiles')
    .select('*')
    .order('created_at', { ascending: false });

  if (profilesError) throw profilesError;

  const [{ data: roles, error: rolesError }, { data: perms, error: permsError }] = await Promise.all([
    supabase.from('user_roles').select('*'),
    supabase.from('user_permissions').select('user_id, permission'),
  ]);

  if (rolesError) throw rolesError;
  if (permsError) throw permsError;

  const all: UserWithRole[] = profiles.map((profile: any) => {
    const userRole = roles.find(r => r.user_id === profile.id);
    return {
      id: profile.id,
      email: profile.email,
      full_name: profile.full_name,
      phone: profile.phone,
      created_at: profile.created_at,
      last_login_at: profile.last_login_at ?? null,
      role: userRole?.role || 'csr',
      permissions: (perms ?? []).filter(p => p.user_id === profile.id).map(p => p.permission).filter(isPermission),
      deactivated_at: profile.deactivated_at ?? null,
    };
  });

  return includeDeactivated
    ? all.filter(u => u.deactivated_at !== null)
    : all.filter(u => u.deactivated_at === null);
}

export function useUsers() {
  return useQuery({
    queryKey: ['users', 'active'],
    queryFn: () => fetchUsers(false),
  });
}

export function useDeactivatedUsers() {
  return useQuery({
    queryKey: ['users', 'deactivated'],
    queryFn: () => fetchUsers(true),
  });
}

export function useUpdateUserRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, newRole }: { userId: string; newRole: 'admin' | 'csr' }) => {
      const { error: deleteError } = await supabase
        .from('user_roles')
        .delete()
        .eq('user_id', userId);

      if (deleteError) throw deleteError;

      const { error: insertError } = await supabase
        .from('user_roles')
        .insert({ user_id: userId, role: newRole } as any);

      if (insertError) throw insertError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast({ title: 'User role updated successfully' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to update role',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

/** Replace a user's grants with exactly `permissions` (empty = none). Admin-only by RLS. */
export function useSetUserPermissions() {
  const queryClient = useQueryClient();
  const { tenantId } = useTenant();

  return useMutation({
    mutationFn: async ({ userId, permissions }: { userId: string; permissions: Permission[] }) => {
      if (!tenantId) throw new Error('No company in session');
      const { data: current, error: readError } = await supabase
        .from('user_permissions')
        .select('id, permission')
        .eq('user_id', userId)
        .eq('company_id', tenantId);
      if (readError) throw readError;

      const have = new Set((current ?? []).map((p) => p.permission));
      const removeIds = (current ?? []).filter((p) => !permissions.includes(p.permission as Permission)).map((p) => p.id);
      const add = permissions.filter((p) => !have.has(p));

      if (removeIds.length) {
        const { error } = await supabase.from('user_permissions').delete().in('id', removeIds);
        if (error) throw error;
      }
      if (add.length) {
        const { error } = await supabase
          .from('user_permissions')
          .insert(add.map((permission) => ({ company_id: tenantId, user_id: userId, permission })));
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast({ title: 'Permissions updated' });
    },
    onError: (error: Error) => {
      toast({ title: 'Failed to update permissions', description: error.message, variant: 'destructive' });
    },
  });
}

export function useSetUserActive() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, active }: { userId: string; active: boolean }) => {
      // Use raw fetch instead of supabase.functions.invoke so we can read the
      // response body when the function returns 4xx/5xx (invoke swallows it).
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;
      const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
      const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

      const res = await fetch(`${SUPABASE_URL}/functions/v1/set-user-active`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: SUPABASE_KEY,
          Authorization: `Bearer ${accessToken ?? SUPABASE_KEY}`,
        },
        body: JSON.stringify({ userId, active }),
      });

      const raw = await res.text();
      let parsed: any = null;
      try { parsed = raw ? JSON.parse(raw) : null; } catch { /* non-JSON body */ }

      if (!res.ok) {
        const msg = parsed?.error || raw || `Edge function returned ${res.status}`;
        throw new Error(msg);
      }
      if (parsed?.error) throw new Error(parsed.error);

      return parsed;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast({
        title: variables.active ? 'User reactivated' : 'User deactivated',
        description: variables.active
          ? 'They can sign in again and appear in the active users list.'
          : 'They can no longer sign in. Their work history is preserved.',
      });
    },
    onError: (error: Error, variables) => {
      toast({
        title: variables.active ? 'Failed to reactivate user' : 'Failed to deactivate user',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}
